// Copyright (c) Docugami, Inc. All rights reserved.

import { IStyle, TooltipHost } from "@fluentui/react";
import { DgFlexCenteredRow } from "components/common/DgCommonStyledComponents";
import { userFontFamily } from "components/common/Fonts";
import { greyText, primaryTheme, primaryThemeSettings } from "components/shell/presentation/AppStyles";
import { DgIconButton } from "components/shell/utils/DgIconButton";
import { ItemTypeFilter, SelectionMode, StorageProviderFilePicker } from "components/storage/StorageProviderFilePicker";
import { dgIconNames } from "hooks/dgIcons";
import { ProviderId } from "networking/StorageProviders";
import { useState } from "react";
import styled from "styled-components";
import { useFormattedPath } from "hooks/FormattedPath";
import { ProviderPickerItem } from "components/storage/ProviderPickerItem";

const DestinationTextContainer = styled(DgFlexCenteredRow)`
    font-family: ${userFontFamily};
    font-size: 18px;
    font-weight: 400;
    color: ${primaryThemeSettings.palette.black};
    white-space: nowrap;
    display: flex;
    gap: 5px;
    .hoverVisibility {
        visibility: hidden;
    }
    :hover {
        .hoverVisibility {
            visibility: visible;
        }
    }
`;

interface ProviderDestinationProps {
    providerId: ProviderId;
    destinationPath: string;
    onSelected?: (item: ProviderPickerItem) => void;
    readonly?: boolean;
}

export const ProviderDestination = (props: ProviderDestinationProps): JSX.Element => {
    const [isActive, setIsActive] = useState(false);
    const formattedPath = useFormattedPath({ providerId: props.providerId, destinationPath: props.destinationPath, separatorString: ' > ' });

    function onConfirm(items: ProviderPickerItem[]) {
        if (items.length > 0) {
            props.onSelected?.(items[0]);
        }

        setIsActive(false);
    }

    const rootStyle: IStyle = {
        whiteSpace: 'nowrap',
        overflowX: 'hidden',
        textOverflow: 'ellipsis',
        direction: 'rtl',
        textAlign: 'left',
        marginRight: '5px'
    }

    return (
        <DestinationTextContainer className='destination middleItem'>
            <TooltipHost styles={{ root: rootStyle }} content={`${formattedPath.providerString}${formattedPath.directoryPath}`}>
                <span>{formattedPath.providerString}</span>
                <span>&lrm;{formattedPath.directoryPath}&lrm;</span>
            </TooltipHost>
            {!props.readonly &&
                <>
                    <div className="hoverVisibility">
                        <DgIconButton iconName={dgIconNames.DgPen} height={15} width={15} color={greyText} shadow={primaryTheme.palette.black} onClick={() => setIsActive(true)} />
                    </div>
                    <StorageProviderFilePicker providerId={props.providerId} isOpen={isActive} onCancel={() => setIsActive(false)} onConfirm={onConfirm} itemTypeFilter={ItemTypeFilter.Folder} selectionMode={SelectionMode.Single} />
                </>
            }
        </DestinationTextContainer>
    );
}
